/**
 * Admin Dashboard shared UI primitives.
 */

import React from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Pagination } from './types';

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; cls: string }> = {
  completed: {
    icon: <CheckCircle className="w-3.5 h-3.5" />,
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  failed: {
    icon: <XCircle className="w-3.5 h-3.5" />,
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  running: {
    icon: <Clock className="w-3.5 h-3.5 animate-pulse" />,
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  pending: {
    icon: <AlertCircle className="w-3.5 h-3.5" />,
    cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  },
};

export function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}
    >
      {cfg.icon}
      {status}
    </span>
  );
}

export function Pager({
  page,
  pagination,
  onPrev,
  onNext,
}: {
  page: number;
  pagination: Pagination | null;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (!pagination || pagination.total <= pagination.limit) return null;
  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
      <span>
        {pagination.offset + 1}–{Math.min(pagination.offset + pagination.limit, pagination.total)}{' '}
        of {pagination.total}
      </span>
      <div className="flex gap-2">
        <button
          onClick={onPrev}
          disabled={page === 0}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={onNext}
          disabled={!pagination.hasMore}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
